import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { AuthFrame } from '@/components/auth/AuthFrame';
import { FormError } from '@/components/common/FormError';
import { OAuthButtons, OrDivider } from '@/components/auth/OAuthButtons';
import { SendAgainButton } from '@/components/invite/SendAgainButton';
import { SignInLinkForm } from '@/components/invite/SignInLinkForm';
import { oauthProviders } from '@/lib/config';

/**
 * self-signup (design section 4): the public door of a new agency's 30-day trial. An emailed link or code
 * that CREATES the account (`createUser: true`), or Google / Apple. Password sign-up stays closed at Caddy
 * on purpose. The link and the OAuth return land on `/signin`, one of the two landing paths
 * (`isLinkLandingPath`), which goes on to the board: an account with no agency gets the no-agency screen
 * and its "Create my agency" form.
 */
export function SignUp() {
  const { t } = useTranslation();
  const { session, sendSignInLink, clearLinkError } = useAuth();
  const [sentTo, setSentTo] = React.useState<string | null>(null);
  const [oauthError, setOauthError] = React.useState<string | null>(null);
  const hasOAuth = Object.values(oauthProviders()).some(Boolean);

  if (session) return <Navigate to="/dashboard" replace />;

  return (
    <AuthFrame
      footer={
        <p>
          {t('auth.signup.haveAccount')}{' '}
          <Link to="/signin" className="font-medium text-accent-base underline-offset-4 hover:underline">
            {t('auth.signup.signIn')}
          </Link>
        </p>
      }
    >
            <h1 className="text-2xl font-semibold text-ink">{t('auth.signup.title')}</h1>
            <p className="mt-1 text-base text-ink-muted">{t('auth.signup.subtitle')}</p>

            {sentTo === null ? (
              <>
                {hasOAuth && (
                  <div className="mt-6 flex flex-col gap-4">
                    <FormError message={oauthError} onDismiss={() => setOauthError(null)} />
                    <OAuthButtons redirectPath="/signin" onError={(key) => setOauthError(t(`auth.errors.${key}`))} />
                    <OrDivider />
                  </div>
                )}
                <div className="mt-6 flex flex-col gap-4">
                  {/* UX audit portal-signup H1/H2: three numbered steps; "in this browser" before sending. */}
                  <ol aria-label={t('auth.signup.stepsLabel')} className="flex flex-col gap-2 text-base text-ink-secondary">
                    {(['enter', 'open', 'name'] as const).map((step, i) => (
                      <li key={step} className="flex items-center gap-2.5">
                        <span
                          aria-hidden
                          className="grid size-5 shrink-0 place-items-center rounded-full bg-accent-wash text-xs font-semibold text-accent-base"
                        >
                          {i + 1}
                        </span>
                        {t(`auth.signup.steps.${step}`)}
                      </li>
                    ))}
                  </ol>
                  <SignInLinkForm
                    idPrefix="signup-link"
                    redirectPath="/signin"
                    createUser
                    onSent={(address) => {
                      clearLinkError();
                      setSentTo(address);
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="mt-6 flex flex-col gap-4">
                <p role="status" className="text-base text-ink">
                  {t('auth.signup.sent', { email: sentTo })}
                </p>
                <SendAgainButton onSend={async () => (await sendSignInLink(sentTo, '/signin', { createUser: true })).error} />
              </div>
            )}
    </AuthFrame>
  );
}
